import { action, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';
import { DataResult, State } from '@servicetitan/data-query';
import { AsyncDataSource, TableState } from '@servicetitan/table';

import { LoadStatus } from '../../../common/enums/load-status';
import { TABLE_PAGE_SIZE } from '../../utils/ux';
import {
    TelecomApi,
    GlobalOptInOut,
    UpdateGlobalOptInOutRequest,
    OptOutFilterType,
} from '../api/telecom.api';
import { GlobalOptOutData } from '../types/global-opt-out-data';
import { createPhoneId, parsePhoneId } from '../utils/phone-id.utils';
import { ManageTenantStore } from '../../stores/manage-tenant.store';
import { NotificationsStore } from '../../../common/stores/notifications.store';

const sortFieldMapping: Record<keyof Omit<GlobalOptOutData, 'id'>, keyof GlobalOptInOut> = {
    phoneNumber: 'phoneNumberE164',
    optedIn: 'isOptedOut',
};

const mapGlobalOptInOutToGlobalOptOutData = (globalOptInOut: GlobalOptInOut): GlobalOptOutData => {
    return {
        id: createPhoneId(globalOptInOut),
        phoneNumber: globalOptInOut.phoneNumberE164 || '',
        optedIn: !globalOptInOut.isOptedOut, // Invert: isOptedOut = false means optedIn = true
    };
};

@injectable()
export class GlobalOptOutStore {
    @observable loadStatus = LoadStatus.None;
    @observable tableState = new TableState<GlobalOptOutData>({
        pageSize: TABLE_PAGE_SIZE,
    });
    @observable initialised = false;

    @observable phoneNumberSearch = '';
    @observable optInFilter: OptOutFilterType[] = [];

    constructor(
        @inject(TelecomApi) private telecomApi: TelecomApi,
        @inject(ManageTenantStore) private manageTenantStore: ManageTenantStore,
        @inject(NotificationsStore) private notificationsStore: NotificationsStore,
    ) {
        makeObservable(this);
    }

    @action init = () => {
        if (!this.initialised) {
            this.initialised = true;
            this.tableState.setDataSource(new AsyncDataSource({ get: this.fetchGlobalOptOuts }));
        }
    };

    fetchGlobalOptOuts = async (state: State): Promise<DataResult<GlobalOptOutData>> => {
        const search = this.phoneNumberSearch;
        const filter = this.optInFilter.length === 1 ? this.optInFilter[0] : undefined;

        try {
            runInAction(() => {
                this.loadStatus = LoadStatus.Loading;
            });

            const skip = state.skip ?? 0;
            const take = state.take ?? TABLE_PAGE_SIZE;
            const page = Math.floor(skip / TABLE_PAGE_SIZE) + 1;
            const pageSize = take;

            let sortField: string | undefined;
            let isAscending: boolean | undefined;

            if (state.sort?.[0]) {
                const field = state.sort[0].field;
                const backendField =
                    field in sortFieldMapping
                        ? sortFieldMapping[field as keyof typeof sortFieldMapping]
                        : field;

                sortField = backendField.charAt(0).toUpperCase() + backendField.slice(1);
                isAscending = state.sort[0].dir === 'asc';
            }

            const response = await this.telecomApi.globalOptInOutsGET(
                page,
                pageSize,
                isAscending,
                sortField,
                search || undefined,
                filter,
                this.manageTenantStore.tenant!.internalId,
                this.manageTenantStore.tenant!.partnerId,
            );

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });

            return {
                data: response.data?.items?.map(mapGlobalOptInOutToGlobalOptOutData) || [],
                total: response.data?.count || 0,
            };
        } catch {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });

            return {
                data: [],
                total: 0,
            };
        }
    };

    filterByPhoneNumber = (phoneNumberSearch: string) => {
        runInAction(() => {
            this.phoneNumberSearch = phoneNumberSearch;
        });

        this.fetchTableDataFromStart();
    };

    filterByOptInStatus = (optInFilter: OptOutFilterType[]) => {
        runInAction(() => {
            this.optInFilter = optInFilter;
        });

        this.fetchTableDataFromStart();
    };

    updateOptInStatus = async (globalOptOutData: GlobalOptOutData, optedIn: boolean) => {
        try {
            runInAction(() => {
                this.loadStatus = LoadStatus.Loading;
            });

            const { ownerId, phoneNumberE164 } = parsePhoneId(globalOptOutData.id);

            const updateRequest = new UpdateGlobalOptInOutRequest({
                ownerId,
                phoneNumberE164,
                isOptedOut: !optedIn, // Invert: optedIn = true means isOptedOut = false
            });

            await this.telecomApi.globalOptInOutsPATCH(
                this.manageTenantStore.tenant!.internalId,
                this.manageTenantStore.tenant!.partnerId,
                updateRequest,
            );

            this.notificationsStore.notifyInSnackbar({
                title: optedIn ? 'Phone number opted in' : 'Phone number opted out',
            });

            await this.tableState.fetchData();

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });
        } catch (error) {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });
            throw error;
        }
    };

    fetchTableDataFromStart = () => this.tableState.fetchData({ newSkip: 0 });

    get isLoading() {
        return this.loadStatus === LoadStatus.Loading;
    }
}
